import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useBranches } from '@/hooks/useBranches';
import type { Branch } from '@/types';

interface BranchContextType {
    selectedBranch: string | null; // null means "All Branches"
    setSelectedBranch: (branchId: string | null) => void;
    branches: Branch[];
    loading: boolean;
    currentBranch: Branch | null;
}

const BranchContext = createContext<BranchContextType | undefined>(undefined);

const BRANCH_STORAGE_KEY = 'adjja_selected_branch';

export const BranchProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { branches, loading } = useBranches();
    const [selectedBranch, setSelectedBranchState] = useState<string | null>(() => {
        // Initialize from localStorage
        const stored = localStorage.getItem(BRANCH_STORAGE_KEY);
        return stored || null;
    });

    // Auto-select first branch if none selected and branches are loaded
    // Auto-select first branch if none selected and branches are loaded
    // DISABLED: User requested to remove branch selection relation
    /*
    useEffect(() => {
        if (!loading && branches.length > 0 && selectedBranch === null) {
            const firstBranch = branches[0];
            setSelectedBranchState(firstBranch.id);
            localStorage.setItem(BRANCH_STORAGE_KEY, firstBranch.id);
        }
    }, [loading, branches, selectedBranch]);
    */

    // Memoize setSelectedBranch to prevent recreation
    const setSelectedBranch = useCallback((branchId: string | null) => {
        setSelectedBranchState(branchId);
        if (branchId === null) {
            localStorage.removeItem(BRANCH_STORAGE_KEY);
        } else {
            localStorage.setItem(BRANCH_STORAGE_KEY, branchId);
        }
    }, []);

    // Memoize currentBranch to prevent unnecessary recalculations
    const currentBranch = useMemo(() => {
        return selectedBranch
            ? branches.find(b => b.id === selectedBranch) || null
            : null;
    }, [selectedBranch, branches]);

    // Memoize the context value to prevent unnecessary re-renders
    const value = useMemo(() => ({
        selectedBranch,
        setSelectedBranch,
        branches,
        loading,
        currentBranch
    }), [selectedBranch, setSelectedBranch, branches, loading, currentBranch]);

    return (
        <BranchContext.Provider value={value}>
            {children}
        </BranchContext.Provider>
    );
};

export const useBranchContext = () => {
    const context = useContext(BranchContext);
    if (context === undefined) {
        throw new Error('useBranchContext must be used within a BranchProvider');
    }
    return context;
};
